import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { parseHashParams } from '../../utils/hashRouter';
//
import Layout from '../../components/Layout';
import { Title } from '../../components/ui';
import CheckoutProgressbar from '../../components/CheckoutProgressbar';
import ConfirmView from './components/ConfirmView';
import PaymentView from './components/Payment/PaymentView';
import CompleteView from './components/CompleteView';
//
import { setShowLoading } from '../../redux/systemInfo';
import { getOrderCost } from '../../api/services/utilService';
import { placeOrder } from '../../api/services/paymentService';
import { sendOrderConfirmationEmail } from '../../api/services/orderService';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  height: auto;
  padding: 50px 0;
  align-items: center;

  @media (max-width: 768px) {
    padding: 30px 0;
  }

  @media (max-width: 480px) {
    padding: 20px 0;
  }
`;

const PageTitle = styled(Title)`
  width: 100%;
  text-align: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const MainContent = styled.div`
  display: flex;
  width: 100%;
  max-width: 1450px;
  gap: 2rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 200px;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    flex-direction: row;
    min-width: auto;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { comment, code } = location.state || {};
  const userInfo = useSelector((state) => state.userInfo);
  const userCart = useSelector((state) => state.userCart);
  const navigate = useNavigate();

  const [soNo, setSoNo] = useState();
  const [poNo, setPoNo] = useState('');
  const [shipToInfo, setShipToInfo] = useState();
  const [paymentInfo, setPaymentInfo] = useState();
  const [billToInfo, setBillToInfo] = useState();
  //
  const [currentStep, setCurrentStep] = useState(1);
  const [totalItemPrice, setTotalItemPrice] = useState(0);
  const [totalTaxPrice, setTotalTaxPrice] = useState(0);
  const [totalShippingPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  const fromIdSO = useRef('');
  const calculateCartSubtotal = () =>
    userCart.cart_list.reduce((sum, item) => sum + item.price * item.totQty, 0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token === 'null') {
      navigate('/');
    } else {
      getDbCost();
    }
  }, [userCart.cart_list]);

  useEffect(() => {
    const handleBack = () => {
      if (currentStep === 1) {
        navigate(-1);
      } else {
        setCurrentStep(currentStep - 1);
      }
    };

    window.addEventListener('popstate', handleBack);

    return () => {
      window.removeEventListener('popstate', handleBack);
    };
  }, []);

  useEffect(() => {
    const { params } = parseHashParams();
    const from = params.get('from');
    if (from) {
      fromIdSO.current = from;
    }
  }, [window.location.hash]);

  const getDbCost = async () => {
    dispatch(setShowLoading({ showLoading: true }));
    const fallbackSubtotal = calculateCartSubtotal();

    try {
      const result = await getOrderCost(userInfo.idCustomer, {
        sessionID: userInfo.idCustomer,
        couponCd: code,
        point: 0,
        shipFee: 0,
        otherFee: 0,
        zipcode: userInfo.zipcode,
        idStore: userInfo.idStore,
      });
      const { output_subTotAmt, output_taxAmt, output_totAmt } =
        result.orderAmount;

      const hasValidSubtotal =
        Number(output_subTotAmt) > 0 || fallbackSubtotal === 0;

      if (hasValidSubtotal) {
        setTotalItemPrice(output_subTotAmt);
        setTotalTaxPrice(output_taxAmt);
        setTotalPrice(output_totAmt);
      } else {
        setTotalItemPrice(fallbackSubtotal);
        setTotalTaxPrice(0);
        setTotalPrice(fallbackSubtotal);
      }
    } catch (error) {
      console.error('Error fetching checkout cost:', error);
      setTotalItemPrice(fallbackSubtotal);
      setTotalTaxPrice(0);
      setTotalPrice(fallbackSubtotal);
    } finally {
      dispatch(setShowLoading({ showLoading: false }));
    }
  };

  const handlePlaceOrder = async () => {
    try {
      const isPaymentTermSelected = paymentInfo?.isPaymentTerm;

      const result = await placeOrder({
        custPO: poNo,
        refIdSO: fromIdSO.current,
        idCustomer: userInfo.idCustomer,
        sessionID: userInfo.idCustomer,
        brand: userInfo.brandId,
        idStore: userInfo.idStore,
        discount: 0,
        discountPct: 0,
        discountNote: '',
        shipVia: 3210,
        customCarrier: '',
        carrierAcct: '',
        billTPAddr: '',
        shipNote: '',
        paymentType: isPaymentTermSelected
          ? 3215
          : paymentInfo.idCC != -1
            ? 58
            : 3215,
        paymentCard: '',
        paypalUserId: '', // TODO ?
        shipFee: totalShippingPrice,
        salesTaxCd: shipToInfo.states,
        salesTax: totalTaxPrice,
        idCC: isPaymentTermSelected ? 0 : paymentInfo.idCC,
        paymentTerm: isPaymentTermSelected ? paymentInfo.paymentTermCode : null,
        auth_transaction_id: '', // TODO ?
        bill_firstNm: billToInfo.firstNm || '',
        bill_lastNm: billToInfo.lastNm || '',
        bill_addr: billToInfo.addr || '',
        bill_addr2: '',
        bill_city: billToInfo.city,
        bill_state: billToInfo.states,
        bill_zipcode: billToInfo.zipcode,
        bill_country: billToInfo.country,
        bill_phone: billToInfo.tel,
        bill_company: billToInfo.companyNm,
        ship_firstNm: shipToInfo.firstNm || '',
        ship_lastNm: shipToInfo.lastNm || '',
        ship_addr: shipToInfo.addr || '',
        ship_addr2: '',
        ship_city: shipToInfo.city,
        ship_state: shipToInfo.states,
        ship_zipcode: shipToInfo.zipcode,
        ship_country: shipToInfo.country,
        ship_phone: shipToInfo.tel,
        ship_company: shipToInfo.companyNm,
        couponCd: code || '',
        types: '3',
        descr: '', // TODO : ACH 에 쓰는듯
        shippingComment: comment || '',
        issuer: userInfo.userId,
        issueIdStore: userInfo.idStore,
      });

      if (result.is_success) {
        setSoNo(result.idSO);

        // Get additional customer information for email
        try {
          // Prepare order data for email
          const orderData = {
            orderNumber: result.idSO,
            customerEmail: userInfo.email || null,
            salesRepEmail: null,
            orderDetails: {
              items: userCart.cart_list,
              totalItemPrice,
              totalTaxPrice,
              totalShippingPrice,
              totalPrice,
              poNo,
              couponCode: code,
              shippingComment: comment,
            },
            shippingInfo: shipToInfo,
            billingInfo: billToInfo,
            paymentInfo: paymentInfo,
            orderDate: new Date().toISOString(),
          };

          // Send order confirmation email
          await sendOrderConfirmationEmail(orderData);
        } catch (emailError) {
          console.error('Error sending order confirmation email:', emailError);
          // Don't block the order completion if email fails
        }

        setCurrentStep(3);
      } else {
        alert(`Error while create SO! ${result.message || ''}`);
      }
    } catch (error) {
      alert(`Error creating order: ${error.message}`);
    }
  };

  return (
    <Layout>
      <Container>
        <PageTitle>
          {currentStep === 3
            ? 'Thank you for your order'
            : currentStep === 1
              ? 'Payment Information'
              : 'Confirm your order'}
        </PageTitle>
        <MainContent>
          <ProgressSection>
            <CheckoutProgressbar currentStep={currentStep} horizontal={false} />
          </ProgressSection>
          <ContentSection>
            {currentStep === 1 ? (
              <PaymentView
                cartList={userCart.cart_list}
                setCurrentStep={setCurrentStep}
                poNo={poNo}
                setPoNo={setPoNo}
                totalItemPrice={totalItemPrice}
                totalTaxPrice={totalTaxPrice}
                totalShippingPrice={totalShippingPrice}
                totalPrice={totalPrice}
                setShipToInfo={setShipToInfo}
                setPaymentInfo={setPaymentInfo}
                setBillToInfo={setBillToInfo}
              />
            ) : currentStep === 2 ? (
              <ConfirmView
                cartList={userCart.cart_list}
                setCurrentStep={setCurrentStep}
                handlePlaceOrder={handlePlaceOrder}
                shipToInfo={shipToInfo}
                paymentInfo={paymentInfo}
                billToInfo={billToInfo}
                totalItemPrice={totalItemPrice}
                totalTaxPrice={totalTaxPrice}
                totalShippingPrice={totalShippingPrice}
                totalPrice={totalPrice}
              />
            ) : (
              <CompleteView
                cartList={userCart.cart_list}
                setCurrentStep={setCurrentStep}
                soNo={soNo}
              />
            )}
          </ContentSection>
        </MainContent>
      </Container>
    </Layout>
  );
};

export default CheckoutPage;
